import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Divider,
    Grid,
    Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RootStoreContext } from '../../../global/storeContext';
import { IDTransactionLog, IProfile, TransactionStatus } from 'realhaus-sdk';
import { BankInfoResponse } from '../../../utils/apiClients';

export const PayoutDrawer: React.FC<{
    request: IDTransactionLog;
    trxLogId: string;
    onPayoutComplete: () => void;
}> = observer(({ trxLogId, request, onPayoutComplete }) => {
    const [bankAccountInfo, setBankAccountInfo] = React.useState<BankInfoResponse>();
    const [userProfile, setUserProfile] = React.useState<IProfile>();
    const { uiStore, dashboardStore } = React.useContext(RootStoreContext);

    const getAccounts = () => {
        uiStore.showLoading();
        dashboardStore
            .getBankAccountInfos(request.senderId)
            .then((acctInfo) => {
                if (!!acctInfo) {
                    setBankAccountInfo(acctInfo);
                } else {
                    uiStore.error('No bank account information');
                }
            })
            .catch((err) => {
                uiStore.error('Could not fetch bank information');
            })
            .finally(() => {
                uiStore.hideLoading();
            });
    };

    const getUserProfile = () => {
        uiStore.showLoading();
        dashboardStore
            .getUserProfile(request.senderId)
            .then((profile) => {
                if (!!profile) {
                    setUserProfile(profile);
                } else {
                    uiStore.error('Could not get user profile');
                }
            })
            .catch((err) => {
                uiStore.error('Could not fetch user profile');
            })
            .finally(() => {
                console.log(userProfile);
                uiStore.hideLoading();
            });
    };

    const handlePaid = async () => {
        uiStore.showLoading();
        try {
            await dashboardStore.changeStatusToPaid(trxLogId).then((res) => {
                if (!!res && res) {
                    uiStore.success('status updated');
                    onPayoutComplete();
                }
            });
        } catch (err) {
            uiStore.error('Failed to change status to paid');
        } finally {
            uiStore.hideLoading();
        }
    };

    React.useEffect(() => {
        getAccounts();
        getUserProfile();
    }, []);

    return (
        <>
            <Grid container mt={12} spacing={3} mb={3}>
                <Grid item xs={12} justifyContent='center' textAlign='center'>
                    {userProfile && (
                        <Typography variant='h6'>
                            <b>{`${userProfile.firstname ?? ''} ${userProfile.lastname ?? ''}`}</b>
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls='request-summary'
                            id='request-summary'
                        >
                            <Typography>Request Summary</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <PayoutSummary req={request} />
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                <Grid item xs={12}>
                    <Accordion defaultExpanded>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls='panel2-content'
                            id='summary'
                        >
                            <Typography>Financial Accounts Information</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <BankAccountInfo acctInfo={bankAccountInfo} />
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                {/*
                {bankAccountInfo?.email && (
                    <Grid item xs={12}>
                        <Typography>
                            <b>Email:</b> {bankAccountInfo.email}
                        </Typography>
                    </Grid>
                )}
                {bankAccountInfo?.bank.institution && (
                    <Grid item xs={12}>
                        <Typography>
                            <b>Bank Account Name:</b> {bankAccountInfo.bank.institution}
                        </Typography>
                    </Grid>
                )}
                {bankAccountInfo?.bank?.eftAccount && (
                    <>
                        <Grid item xs={12}>
                            <Typography>
                                <b>Bank Account:</b> {bankAccountInfo.bank.eftAccount.account}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>
                                {' '}
                                <b>Account Id:</b> {bankAccountInfo.bank.eftAccount.accountId}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>
                                <b>Account Name:</b> {bankAccountInfo.bank.accountDetails.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>
                                {' '}
                                <b>Account Official Name:</b>{' '}
                                {bankAccountInfo.bank.accountDetails.official_name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>
                                {' '}
                                <b>Account Institution No.:</b>{' '}
                                {bankAccountInfo.bank.eftAccount.institution}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>
                                {' '}
                                <b>Account Branch:</b> {bankAccountInfo.bank.eftAccount.branch}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>
                                {' '}
                                <b>Account Type:</b> {bankAccountInfo.bank.accountDetails.type}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography>
                                {' '}
                                <b>Account SubType:</b>
                                {bankAccountInfo.bank.accountDetails.subtype}
                            </Typography>
                        </Grid>
                    </>
                )} */}

                {request && (
                    <Grid item xs={12}>
                        <Button
                            disabled={
                                uiStore.loading || request.status === TransactionStatus.SUCCESS
                            }
                            variant='contained'
                            color='primary'
                            fullWidth
                            onClick={handlePaid}
                        >
                            Mark as Paid
                        </Button>
                    </Grid>
                )}
            </Grid>
        </>
    );
});

const Title = ({ text }: { text: string }) => (
    <Typography variant='subtitle2' fontWeight='600'>
        {text}
    </Typography>
);

const PayoutSummary = ({ req }: { req: IDTransactionLog }) => {
    return (
        <Grid container>
            {req && (
                <>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Transaction Type' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{req.type}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Amount' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{req.amount}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Status' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{req.status}</Typography>
                        </Grid>
                    </Grid>
                </>
            )}
        </Grid>
    );
};

const BankAccountInfo = ({ acctInfo }: { acctInfo?: BankInfoResponse }) => {
    return (
        <Grid container spacing={2}>
            {acctInfo?.email && (
                <Grid container item xs={12}>
                    <Grid item xs={4}>
                        <Title text='Email' />
                    </Grid>
                    <Grid item xs={8}>
                        <Typography variant='body2'>{acctInfo.email}</Typography>
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12}>
                <Divider />
            </Grid>
            {acctInfo?.bank?.eftAccount && (
                <Grid container item xs={12}>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Institution' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>{acctInfo.bank.institution}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Account ID' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {acctInfo.bank.eftAccount.accountId}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Account Number' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {acctInfo.bank.eftAccount.account}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Account Name' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {acctInfo.bank.accountDetails?.name}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Account Official Name' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {acctInfo.bank.accountDetails?.official_name}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Institution No.' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {acctInfo.bank.eftAccount.institution}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Branch' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {acctInfo.bank.eftAccount.branch}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Account Type' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {acctInfo.bank.accountDetails?.type}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12}>
                        <Grid item xs={4}>
                            <Title text='Account Subtype' />
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant='body2'>
                                {acctInfo.bank.accountDetails?.subtype}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};
